import { ProductVariant } from "../types/productVariant";

export const VARIANT_NAME_KEY = "__name";
export const MAX_VARIANT_DIMENSIONS = 3;

export type VariantDimensionValue = {
  value: string;
  isNew: boolean;
};

export type VariantDimension = {
  key: string;
  values: VariantDimensionValue[];
};

export type VariantOptionRow = {
  key: string;
  comboKey: string;
  values: string[];
  existing: ProductVariant | null;
  checked: boolean;
};

const cartesianProduct = <T>(arrays: T[][]): T[][] => {
  if (arrays.length === 0) return [];
  return arrays.reduce<T[][]>(
    (acc, values) =>
      acc.flatMap((combination) =>
        values.map((value) => [...combination, value]),
      ),
    [[]],
  );
};

const uniqueValues = (values: Array<string | null>) =>
  values.filter(
    (value, index): value is string =>
      !!value && values.indexOf(value) === index,
  );

export const toVariantComboKey = (values: string[]) => JSON.stringify(values);

export const getVariantOptionValue = (
  variant: ProductVariant,
  key: string,
): string | null => {
  const value =
    key === VARIANT_NAME_KEY
      ? variant.name
      : (variant.extraFields?.[key] ?? null);
  return value?.trim() || null;
};

const setVariantOptionValue = (
  variant: ProductVariant,
  key: string,
  value: string | null,
): ProductVariant => {
  if (key === VARIANT_NAME_KEY) {
    return { ...variant, name: value };
  }
  const extraFields = { ...(variant.extraFields ?? {}) };
  if (value) {
    extraFields[key] = value;
  } else {
    delete extraFields[key];
  }
  return { ...variant, extraFields };
};

/** The variant's name, else its option values joined, else null. */
export const variantOptionsLabel = (
  variant: ProductVariant,
  extraFieldKeys: string[],
): string | null =>
  getVariantOptionValue(variant, VARIANT_NAME_KEY) ??
  (extraFieldKeys
    .map((key) => getVariantOptionValue(variant, key))
    .filter((value): value is string => value !== null)
    .join(" / ") ||
    null);

const dimensionOf = (
  variants: ProductVariant[],
  key: string,
): VariantDimension => ({
  key,
  values: uniqueValues(
    variants.map((variant) => getVariantOptionValue(variant, key)),
  ).map((value) => ({ value, isNew: false })),
});

const deriveVariantDimensions = (
  variants: ProductVariant[],
  keys: string[],
): VariantDimension[] => {
  if (variants.length === 0) return [];
  return keys
    .filter((key) =>
      variants.every((variant) => getVariantOptionValue(variant, key) !== null),
    )
    .map((key) => dimensionOf(variants, key));
};

export const initialVariantDimensions = (
  variants: ProductVariant[],
  extraFieldKeys: string[],
): VariantDimension[] => {
  const extraFieldDimensions = deriveVariantDimensions(
    variants,
    extraFieldKeys,
  );
  return extraFieldDimensions.length > 0
    ? extraFieldDimensions
    : deriveVariantDimensions(variants, [VARIANT_NAME_KEY]);
};

export const buildVariantOptionRows = ({
  variants,
  dimensions,
  uncheckedVariantIds,
  toggledNewRows,
}: {
  variants: ProductVariant[];
  dimensions: VariantDimension[];
  uncheckedVariantIds: string[];
  toggledNewRows: string[];
}): { rows: VariantOptionRow[]; otherVariants: ProductVariant[] } => {
  const activeDimensions = dimensions.filter(
    (dimension) => dimension.values.length > 0,
  );
  const matched = new Map<string, ProductVariant[]>();
  const otherVariants: ProductVariant[] = [];

  variants.forEach((variant) => {
    const values = activeDimensions.map((dimension) =>
      getVariantOptionValue(variant, dimension.key),
    );
    const fitsDimensions =
      activeDimensions.length > 0 &&
      activeDimensions.every((dimension, index) =>
        dimension.values.some((item) => item.value === values[index]),
      );
    if (!fitsDimensions) {
      otherVariants.push(variant);
      return;
    }
    const comboKey = toVariantComboKey(values as string[]);
    matched.set(comboKey, [...(matched.get(comboKey) ?? []), variant]);
  });

  const rows = cartesianProduct(
    activeDimensions.map((dimension) => dimension.values),
  ).flatMap((combination): VariantOptionRow[] => {
    const values = combination.map((item) => item.value);
    const comboKey = toVariantComboKey(values);
    const existingVariants = matched.get(comboKey) ?? [];
    if (existingVariants.length > 0) {
      return existingVariants.map((variant) => ({
        key: variant.id,
        comboKey,
        values,
        existing: variant,
        checked: !uncheckedVariantIds.includes(variant.id),
      }));
    }
    const defaultChecked = combination.some((item) => item.isNew);
    return [
      {
        key: comboKey,
        comboKey,
        values,
        existing: null,
        checked: toggledNewRows.includes(comboKey)
          ? !defaultChecked
          : defaultChecked,
      },
    ];
  });

  return { rows, otherVariants };
};

export const createVariantFromOptionRow = (
  row: VariantOptionRow,
  dimensions: VariantDimension[],
  id: ProductVariant["id"],
): ProductVariant =>
  dimensions
    .filter((dimension) => dimension.values.length > 0)
    .reduce<ProductVariant>(
      (variant, dimension, index) =>
        setVariantOptionValue(
          variant,
          dimension.key,
          row.values[index] ?? null,
        ),
      { id, name: null, extraFields: {}, stockCount: null },
    );

const originalOptionValue = (
  originals: ProductVariant[],
  variant: ProductVariant,
  key: string,
) => {
  const original = originals.find((item) => item.id === variant.id);
  return original ? getVariantOptionValue(original, key) : null;
};

export const variantsNeedDimensionDefault = (
  variants: ProductVariant[],
  originals: ProductVariant[],
  key: string,
): boolean =>
  variants.some(
    (variant) =>
      getVariantOptionValue(variant, key) === null &&
      originalOptionValue(originals, variant, key) === null,
  );

export const addVariantDimension = (
  variants: ProductVariant[],
  originals: ProductVariant[],
  key: string,
  defaultValue: string | null,
): { variants: ProductVariant[]; dimension: VariantDimension } => {
  const updated = variants.map((variant) => {
    const value =
      getVariantOptionValue(variant, key) ??
      originalOptionValue(originals, variant, key) ??
      defaultValue;
    return value ? setVariantOptionValue(variant, key, value) : variant;
  });
  return { variants: updated, dimension: dimensionOf(updated, key) };
};

export const removeVariantDimension = (
  variants: ProductVariant[],
  originals: ProductVariant[],
  key: string,
): ProductVariant[] =>
  variants.map((variant) =>
    originalOptionValue(originals, variant, key) === null
      ? setVariantOptionValue(variant, key, null)
      : variant,
  );

export type VariantOptionsRenameState = {
  variants: ProductVariant[];
  dimensions: VariantDimension[];
  toggledNewRows: string[];
};

/** Renames a dimension value everywhere, or returns null when the new value is empty, unchanged or taken. */
export const renameVariantOption = (
  state: VariantOptionsRenameState,
  key: string,
  from: string,
  input: string,
): VariantOptionsRenameState | null => {
  const to = input.trim();
  const dimension = state.dimensions.find((item) => item.key === key);
  if (
    !to ||
    to === from ||
    dimension?.values.some((item) => item.value === to)
  ) {
    return null;
  }
  const activeIndex = state.dimensions
    .filter((item) => item.values.length > 0)
    .findIndex((item) => item.key === key);
  return {
    variants: state.variants.map((variant) =>
      getVariantOptionValue(variant, key) === from
        ? setVariantOptionValue(variant, key, to)
        : variant,
    ),
    dimensions: state.dimensions.map((item) =>
      item.key === key
        ? {
            ...item,
            values: item.values.map((entry) =>
              entry.value === from ? { ...entry, value: to } : entry,
            ),
          }
        : item,
    ),
    toggledNewRows: state.toggledNewRows.map((comboKey) => {
      const values: string[] = JSON.parse(comboKey);
      if (values[activeIndex] !== from) return comboKey;
      values[activeIndex] = to;
      return toVariantComboKey(values);
    }),
  };
};
